import JSZip from 'jszip';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { diffIndices } from 'node-diff3';
const W = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
const serializer = new XMLSerializer();
const xml = node => serializer.serializeToString(node);
const normalize = text => text.replace(/\s+/g, ' ').trim();
function parse(markdown) {
  const template = document.createElement('template');
  const imageSources = new Map(), imagePrefix = '/docworks-word-image/';
  const compact = markdown.replace(/data:image\/[\w.+-]+;base64,[A-Za-z0-9+/=]+/g, source => { if (!imageSources.has(source)) imageSources.set(source, imagePrefix + imageSources.size); return imageSources.get(source); });
  template.innerHTML = DOMPurify.sanitize(marked.parse(compact), { USE_PROFILES: { html: true } });
  const root = template.content;
  const selector = 'p,h1,h2,h3,h4,h5,h6,li,td,th';
  const blocks = [...root.querySelectorAll(selector)].filter(node => !node.querySelector(selector)).map(node => {
    const segments = [];
    const walk = (child, bold = false, italic = false) => {
      if (child.nodeType === 3) { if (child.textContent) segments.push({ text: child.textContent, bold, italic }); return; }
      if (child.nodeType !== 1 || child.tagName === 'IMG') return;
      if (child.tagName === 'BR') { segments.push({ text: '\n', bold, italic }); return; }
      for (const inner of child.childNodes) walk(inner, bold || ['B', 'STRONG'].includes(child.tagName), italic || ['I', 'EM'].includes(child.tagName));
    };
    walk(node);
    return { kind: node.tagName, segments, text: segments.map(s => s.text).join('') };
  }).filter(b => normalize(b.text));
  const originals = new Map([...imageSources].map(([source, key]) => [key, source]));
  return { blocks, images: [...root.querySelectorAll('img')].map(n => originals.get(n.getAttribute('src')) || n.getAttribute('src')), links: [...root.querySelectorAll('a')].map(n => n.getAttribute('href')) };
}
function sourceChars(paragraph) {
  const chars = [];
  for (const run of paragraph.getElementsByTagNameNS(W, 'r')) {
    const properties = [...run.children].find(n => n.localName === 'rPr');
    for (const element of run.children) {
      const text = element.localName === 't' ? element.textContent : element.localName === 'br' ? '\n' : element.localName === 'tab' ? '\t' : '';
      for (const ch of text) chars.push({ ch, properties });
    }
  }
  return chars;
}
function rewrite(paragraph, block, baseline) {
  // Leave drawings, fields, equations, tracked edits and hyperlinks intact rather than rebuilding them.
  const complex = ['drawing', 'pict', 'fldChar', 'instrText', 'hyperlink', 'footnoteReference', 'endnoteReference', 'ins', 'del', 'sdt', 'commentReference', 'commentRangeStart', 'commentRangeEnd'];
  if (complex.some(name => paragraph.getElementsByTagNameNS(W, name).length) || paragraph.getElementsByTagNameNS('http://schemas.openxmlformats.org/officeDocument/2006/math', 'oMath').length) throw new Error('修改的段落含圖片、連結或特殊 Word 物件，無法安全套回原檔；請保留該段，或在 Word 修改。');
  const old = sourceChars(paragraph), next = block.segments.flatMap(segment => [...segment.text].map(ch => ({ ch, bold: segment.bold, italic: segment.italic })));
  const baselineChars = baseline.segments.flatMap(segment => [...segment.text].map(ch => ({ ch, bold: segment.bold, italic: segment.italic })));
  const props = Array(next.length), oldStyles = Array(next.length); let oldPos = 0, newPos = 0;
  const assign = index => { props[newPos] = old[index]?.properties; oldStyles[newPos] = baselineChars[index] || baselineChars.at(-1) || { bold: false, italic: false }; newPos++; };
  for (const hunk of diffIndices(old.map(c => c.ch), next.map(c => c.ch))) {
    while (oldPos < hunk.buffer1[0]) assign(oldPos++);
    for (let index = 0; index < hunk.buffer2[1]; index++) assign(Math.min(old.length - 1, hunk.buffer1[0] + Math.min(index, Math.max(0, hunk.buffer1[1] - 1))));
    oldPos = hunk.buffer1[0] + hunk.buffer1[1];
  }
  while (newPos < next.length) assign(oldPos++);
  const output = paragraph.cloneNode(false), doc = paragraph.ownerDocument;
  const pPr = [...paragraph.children].find(n => n.localName === 'pPr'); if (pPr) output.append(pPr.cloneNode(true));
  for (const marker of [...paragraph.children].filter(n => n.localName === 'bookmarkStart')) output.append(marker.cloneNode(true));
  let run = null, key = null, textNode = null;
  next.forEach((character, index) => {
    const properties = props[index]?.cloneNode(true) || doc.createElementNS(W, 'w:rPr');
    for (const name of ['b', 'bCs', 'i', 'iCs']) {
      const attribute = name[0] === 'b' ? 'bold' : 'italic';
      if (character[attribute] === oldStyles[index][attribute]) continue;
      for (const element of [...properties.children].filter(n => n.localName === name)) element.remove();
      const element = doc.createElementNS(W, 'w:' + name);
      element.setAttributeNS(W, 'w:val', (name[0] === 'b' ? character.bold : character.italic) ? '1' : '0');
      const order = ['rStyle','rFonts','b','bCs','i','iCs','caps','smallCaps','strike','dstrike','outline','shadow','emboss','imprint','noProof','snapToGrid','vanish','webHidden','color','spacing','w','kern','position','sz','szCs','highlight','u','effect','bdr','shd','fitText','vertAlign','rtl','cs','em','lang','eastAsianLayout','specVanish','oMath','rPrChange'];
      properties.insertBefore(element, [...properties.children].find(n => order.indexOf(n.localName) > order.indexOf(name)) || null);
    }
    const signature = xml(properties);
    if (signature !== key || !run) { run = doc.createElementNS(W, 'w:r'); run.append(properties); output.append(run); key = signature; textNode = null; }
    if (character.ch === '\n' || character.ch === '\t') { run.append(doc.createElementNS(W, character.ch === '\n' ? 'w:br' : 'w:tab')); textNode = null; }
    else { if (!textNode) { textNode = doc.createElementNS(W, 'w:t'); textNode.setAttributeNS('http://www.w3.org/XML/1998/namespace', 'xml:space', 'preserve'); run.append(textNode); } textNode.textContent += character.ch; }
  });
  for (const marker of [...paragraph.children].filter(n => n.localName === 'bookmarkEnd')) output.append(marker.cloneNode(true));
  return xml(output);
}
function paragraphSpans(source) {
  const stack = [], spans = [], tags = /<w:p(?=[\s>])[^>]*>|<\/w:p\s*>/g; let match;
  while ((match = tags.exec(source))) {
    if (match[0].startsWith('</')) { const start = stack.pop(); spans.push({ start, end: tags.lastIndex }); }
    else stack.push(match.index);
  }
  return spans.sort((a, b) => a.start - b.start);
}
export async function exportWord(original, baseline, edited) {
  const before = parse(baseline), after = parse(edited);
  if (JSON.stringify(before.images) !== JSON.stringify(after.images) || JSON.stringify(before.links) !== JSON.stringify(after.links)) throw new Error('保留原格式的 Word 匯出支援文字、粗體、斜體及換行；圖片或連結的增刪請在 Word 處理。');
  const zip = await JSZip.loadAsync(await original.arrayBuffer());
  if (!zip.file('word/document.xml')) throw new Error('這不是有效的 Word .docx 原檔');
  if (zip.file('_xmlsignatures/origin.sigs')) throw new Error('此 Word 原檔包含數位簽章，請先另存未簽章的副本。');
  const source = await zip.file('word/document.xml').async('string');
  const documentXml = new DOMParser().parseFromString(source, 'application/xml');
  if (documentXml.getElementsByTagName('parsererror').length) throw new Error('無法讀取原始 Word 內容');
  const paragraphs = [...documentXml.getElementsByTagNameNS(W, 'p')], spans = paragraphSpans(source);
  if (paragraphs.length !== spans.length) throw new Error('原 Word 的段落結構不支援安全匯出');
  const mapping = []; let cursor = 0;
  for (const block of before.blocks) {
    const expected = normalize(block.text); let found = -1;
    for (let index = cursor; index < paragraphs.length; index++) if (normalize(sourceChars(paragraphs[index]).map(c => c.ch).join('')) === expected) { found = index; break; }
    // Some Word objects are only partially represented by the importer. Unchanged
    // blocks stay byte-identical in the original; only changed blocks require mapping.
    mapping.push(found); if (found >= 0) cursor = found + 1;
  }
  const signature = b => JSON.stringify(b), operations = [];
  for (const hunk of diffIndices(before.blocks.map(signature), after.blocks.map(signature))) {
    const [oldIndex, oldCount] = hunk.buffer1, [newIndex, newCount] = hunk.buffer2;
    if (!oldCount || !newCount || (oldCount !== newCount && oldCount !== 1)) throw new Error('這次修改改變了段落結構，無法保證原 Word 格式；請以原段落內修改或換行為主。');
    for (let index = 0; index < oldCount; index++) {
      const sourceIndex = mapping[oldIndex + index];
      if (sourceIndex < 0) throw new Error('無法安全對應修改的 Word 段落：' + before.blocks[oldIndex + index].text.slice(0, 40));
      const paragraph = paragraphs[sourceIndex];
      const replacements = oldCount === 1 ? after.blocks.slice(newIndex, newIndex + newCount) : [after.blocks[newIndex + index]];
      if (replacements.some(b => b.kind !== before.blocks[oldIndex + index].kind)) throw new Error('保留原 Word 排版時請勿改變標題、清單或表格類型');
      operations.push({ ...spans[sourceIndex], text: replacements.map(block => rewrite(paragraph, block, before.blocks[oldIndex + index])).join('') });
    }
  }
  let patched = source;
  for (const operation of operations.sort((a, b) => b.start - a.start)) patched = patched.slice(0, operation.start) + operation.text + patched.slice(operation.end);
  if (operations.length) zip.file('word/document.xml', patched);
  return { blob: operations.length ? await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }) : original, changedParagraphs: operations.length };
}
